import { useMemo, useState } from 'react'
import { Collapse, DatePicker, Descriptions, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  auditMessageText,
  fetchAuditPage,
  type AuditDetailField,
  type AuditEntry,
} from '@/features/audit/auditApi'
import type { Translate } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { defaultDayRange, rangeToIso } from '@/shared/lib/dayRange'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { normalizeObjectType, objectTypeLabel } from '@/shared/lib/objectType'
import { shortId } from '@/shared/lib/shortId'
import { OperationStatusTag } from '@/shared/ui/StatusTag'
import { DataTable } from '@/shared/ui/table/DataTable'
import { PageHeader } from '@/shared/ui/PageHeader'

const { RangePicker } = DatePicker

/** Standart oraliq — oxirgi 7 kun. Katta jurnalni sana filtrisiz so'rash xavfli. */
const defaultRange = (): [Dayjs, Dayjs] => defaultDayRange(7)

/**
 * `actorType`/`targetType` + id — login satri YO'Q, faqat obyekt turi va
 * identifikatori.
 *
 * ⚠️ TUR NOMI XOM CHIQMAYDI (Nuqson #5). Ilgari bu yerda `{type}` to'g'ridan
 * -to'g'ri chizilardi va jurnalda bir qatorda `SUBSCRIBER`, ikkinchisida
 * `subscriber` turardi — bir xil narsa ikki xil ko'rinardi.
 * `objectTypeLabel` shaklni ham, tilni ham bir joyga yig'adi; nima uchun
 * baza ikki xil yozishi `shared/lib/objectType.ts` da yozilgan.
 */
function ActorCell({ type, id, t }: { type: string; id: string | null; t: Translate }) {
  // ⚠️ `SYSTEM` tekshiruvi ham normallashtirilgan qiymat ustidan: aks holda
  // outbox'dan kelgan `System` shakli "tizim" deb tanilmasdan xom teg
  // bo'lib chizilardi.
  if (!id || normalizeObjectType(type) === 'SYSTEM') {
    return (
      <Typography.Text type="secondary" italic>
        {t('audit.systemActor')}
      </Typography.Text>
    )
  }
  return (
    <Space size={4}>
      <Tag>{objectTypeLabel(type, t) ?? t('common.dash')}</Tag>
      <Typography.Text code copyable={{ text: id }} style={{ fontSize: 12 }}>
        {/* ⚠️ Qisqartirish YAGONA joyda — `shared/lib/shortId.ts` (B12).
            Bu yerda SHARTLI shakl, `AgentRef` da esa uchsiz shakl
            turardi: BIR aktyor ikki ekranda ikki xil ko'rinardi.
            Eski shakllarning ro'yxati `shortId.ts` javadoc'ida —
            bu yerda ATAYLAB takrorlanmaydi, chunki `tests/short-id.mjs`
            skaneri manba matnini o'qiydi va izohdagi namuna ham uning
            uchun buzilish bo'lib ko'rinardi. */}
        {shortId(id)}
      </Typography.Text>
    </Space>
  )
}

/**
 * Tafsilotning BITTA qatori — «maydon: bo'lgan → bo'ldi».
 *
 * ⚠️ BUG-044 — bu yerda XOM JSON chizilardi. O'lchangan (2026-08-16,
 * jonli): qator yoyilganda ekranda aynan `{ "source": "PHONE" }` turardi
 * — 2 ta jingalak qavs, qo'shtirnoqli kalit. Endi qavs YO'Q, kalit esa
 * `audit.field.*` orqali tarjima qilinadi.
 *
 * ⚠️ Ichma-ich tuzilma (`nested`) faqat SONI bilan ko'rinadi va odamni
 * texnik blokka yo'naltiradi — sabab `toDetailFields` javadoc'ida.
 */
function DetailRow({ field, t }: { field: AuditDetailField; t: Translate }) {
  const label = t(`audit.field.${field.key}`, { defaultValue: field.key })
  if (field.nested) {
    return (
      <Descriptions.Item key={field.key} label={label}>
        <Space size={6}>
          <Typography.Text>{t('audit.detailItems', { count: Number(field.value) })}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('audit.detailNested')}
          </Typography.Text>
        </Space>
      </Descriptions.Item>
    )
  }
  if (field.before === null) {
    return (
      <Descriptions.Item key={field.key} label={label}>
        {field.value === '' ? <Typography.Text type="secondary">{t('common.dash')}</Typography.Text> : field.value}
      </Descriptions.Item>
    )
  }
  return (
    <Descriptions.Item key={field.key} label={label}>
      {/* «bo'lgan → bo'ldi» — eski qiymat CHIZILGAN, yangisi qalin:
          operator qaysi tomon eski ekanini o'qimasdan ko'radi */}
      <Space size={6}>
        <Typography.Text delete type="secondary">
          {field.before === '' ? t('common.dash') : field.before}
        </Typography.Text>
        <span>→</span>
        <Typography.Text strong>{field.value === '' ? t('common.dash') : field.value}</Typography.Text>
      </Space>
    </Descriptions.Item>
  )
}

/**
 * Boshqaruv amallari jurnali.
 *
 * ⚠️ SANA ORALIG'I MAJBURIY — backend usiz so'rovni rad etadi (audit jadvali
 * cheksiz o'sadi, filtrsiz so'rov butun tarixni tortib olishga urinardi).
 * Shuning uchun `RangePicker` `allowClear={false}` — bo'sh holatga
 * tushirishning iloji yo'q, doim ikkala sana ham to'ldirilgan.
 *
 * ⚠️ ERKIN QIDIRUV MAYDONI YO'Q: backend `action`/`actorType`/`targetType`/
 * `targetId` bo'yicha STRUKTURAVIY filtr qabul qiladi, login bo'yicha erkin
 * qidiruv YO'Q — shuning uchun `DataTable` bu yerda `searchable` EMAS.
 *
 * ⚠️ `total` YO'Q, faqat `hasNext` — `showTotal={false}`, sahifalash
 * tugmalari baribir to'g'ri ishlaydi (`fetchAuditPage` sun'iy son yasaydi,
 * `shared/api/pageResult.ts`ga qarang).
 */
export function AuditPage() {
  const { t } = useTranslation()
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange)

  // ⚠️ TENANT zonasida: `dayjs()` brauzernikini olardi (`dayRange.ts`)
  const { from, to } = rangeToIso(range)

  // Sana o'zgarganda YANGI so'rov: kesh kaliti oraliqqa bog'liq
  // (`queryKeys.audit.list`), aks holda eski davr ma'lumoti ko'rinardi.
  const fetchPage = useMemo(() => fetchAuditPage({ from, to }), [from, to])

  const columns = useMemo<ColumnsType<AuditEntry>>(
    () => [
      {
        key: 'at',
        title: t('audit.columns.at'),
        width: 170,
        render: (_, row) => (
          <Typography.Text title={utcTooltip(row.at)}>{formatDateTime(row.at)}</Typography.Text>
        ),
      },
      {
        key: 'admin',
        title: t('audit.columns.admin'),
        width: 170,
        render: (_, row) => <ActorCell type={row.actorType} id={row.actorId} t={t} />,
      },
      {
        key: 'action',
        title: t('audit.columns.action'),
        width: 200,
        render: (_, row) => t(`audit.action.${row.action}`, { defaultValue: row.action }),
      },
      {
        key: 'username',
        title: t('audit.columns.username'),
        width: 170,
        render: (_, row) =>
          row.targetId ? (
            <ActorCell type={row.targetType ?? t('common.dash')} id={row.targetId} t={t} />
          ) : (
            t('common.dash')
          ),
      },
      {
        key: 'status',
        title: t('audit.columns.status'),
        width: 130,
        render: (_, row) => <OperationStatusTag status={row.result} />,
      },
      {
        key: 'message',
        title: t('audit.columns.message'),
        render: (_, row) => auditMessageText(row, t) ?? t('common.dash'),
      },
    ],
    [t],
  )

  return (
    <>
      <PageHeader
        title={t('nav.audit')}
        description={t('pageIntro.audit')}
        extra={
          <RangePicker
            value={range}
            onChange={(value) => {
              // `allowClear={false}` bilan `value` amalda hech qachon
              // `null` bo'lmaydi, lekin antd tipi baribir shunday ruxsat
              // beradi — shuning uchun himoya shart.
              if (value?.[0] && value[1]) setRange([value[0], value[1]])
            }}
            allowClear={false}
            format="DD.MM.YYYY"
          />
        }
      />

      <DataTable<AuditEntry>
        queryKey={queryKeys.audit.list(from, to)}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="audit"
        showTotal={false}
        scrollX={1150}
        emptyTitle={t('audit.empty')}
        emptyDescription={t('audit.emptyHint')}
        expandable={{
          rowExpandable: (row) => Boolean(row.detail) || Boolean(row.ip),
          /*
            ⚠️ BUG-044 — IKKI QATLAM: yorliqli ro'yxat OCHIQ, xom JSON
            YOPIQ akkordeon ichida.

            Xomini butunlay olib tashlash MUMKIN EMAS: `detail` tuzilmasi
            har amalda boshqa (`AuditEntryView.detail` javadoc'i) va
            yorliqli ro'yxat ichma-ich qismlarni ATAYLAB yoymaydi
            (`toDetailFields`). Nosozlik qidirayotgan muhandisga to'liq
            shakl baribir kerak — u shunchaki BIRINCHI ko'rinish
            bo'lmasligi kerak edi.
          */
          expandedRowRender: (row) => (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {row.detailFields.length > 0 ? (
                <Descriptions
                  size="small"
                  column={1}
                  bordered
                  title={t('audit.detailTitle')}
                  style={{ maxWidth: 720 }}
                >
                  {row.detailFields.map((field) => DetailRow({ field, t }))}
                </Descriptions>
              ) : (
                <Typography.Text type="secondary">{t('audit.noDetail')}</Typography.Text>
              )}

              {row.ip ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('audit.field.ip')}: <Typography.Text code>{row.ip}</Typography.Text>
                </Typography.Text>
              ) : null}

              {row.detail ? (
                <Collapse
                  ghost
                  size="small"
                  items={[
                    {
                      key: 'raw',
                      label: (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {t('audit.detailRaw')}
                        </Typography.Text>
                      ),
                      children: (
                        <Typography.Paragraph
                          code
                          style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                        >
                          {row.detail}
                        </Typography.Paragraph>
                      ),
                    },
                  ]}
                />
              ) : null}
            </Space>
          ),
        }}
      />

      <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
        {t('audit.note')}
      </Typography.Paragraph>
    </>
  )
}
